import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { CANONICAL_FIELDS } from "../../../lib/migrationParse";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

// SCR-P04-11 - one combined header->field mapping across every uploaded file (a header of the
// same name in two files maps once). Starts from the backend's own heuristic detector
// (migration_engine.detect_column_mapping) then lets the user override any guess.
export function MapColumnsStep({ files, mapping, setMapping, onBack, onContinue }) {
  const [detecting, setDetecting] = useState(false);
  const headers = useMemo(() => {
    const set = new Set();
    files.forEach((f) => f.headers.forEach((h) => set.add(h)));
    return Array.from(set);
  }, [files]);

  useEffect(() => {
    if (Object.keys(mapping).length > 0 || headers.length === 0) return;
    setDetecting(true);
    const sampleRows = files[0]?.rows.slice(0, 5) || [];
    api.post("/migration/detect-mapping", { headers, sample_rows: sampleRows })
      .then((r) => setMapping(r.data.mapping))
      .finally(() => setDetecting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers.join(",")]);

  const hasName = Object.values(mapping).includes("name");
  const hasPhone = Object.values(mapping).includes("phone");

  return (
    <div className="space-y-4">
      <div className="solid-surface rounded-[14px] p-5">
        <p className="text-sm font-semibold mb-3" style={{ color: "var(--scarlet-text)" }}>
          {detecting ? "Detecting columns\u2026" : "Confirm what each column means"}
        </p>
        <div className="space-y-2">
          {headers.map((header) => (
            <div key={header} className="flex items-center justify-between gap-3">
              <span className="text-sm font-mono truncate" style={{ color: "var(--scarlet-text)", maxWidth: 220 }}>{header}</span>
              <Select value={mapping[header] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [header]: v === "__none__" ? null : v }))}>
                <SelectTrigger data-testid={MIGRATION.mapFieldSelect(header)} className="w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Don't import</SelectItem>
                  {CANONICAL_FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {(!hasName || !hasPhone) && (
        <div data-testid={MIGRATION.mapUnmappedWarning} className="flex items-start gap-2 rounded-[10px] p-3" style={{ background: "#F5A62322", color: "#B5760A" }}>
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <p className="text-xs">Map a column to <strong>Name</strong> and <strong>Phone</strong> to continue - every row missing either will be flagged, not imported.</p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button data-testid={MIGRATION.wizardContinueButton} disabled={!hasName || !hasPhone} onClick={onContinue} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>
          Continue
        </Button>
      </div>
    </div>
  );
}
